import { defineStore, storeToRefs } from "pinia"
import { computed, ref, watch, shallowRef } from "vue"
import { useRoute } from 'vue-router'
import { useKuradenApiStore, TokenError, type KuraTelJoinRequest } from "./kuradenApi"
import { usePollingsStore, POLLING_LABELS } from "./pollings"
import { useConstructionListsStore } from "./constructionLists"
import { loadedValuePromise } from "@/utils/concurrency"
import { useAccountProfilesStore } from "./accountProfiles"
import { useConstructionStore } from "./construction"
import { useLoginStore } from "./login"
import { useOperationLockStore } from "./operationLock"
import type { IconStyle } from "@/types/accountApiDef"
import type { ConstructionAccountInfo } from "@/types/constructionApiDef"
import { exhaustiveCheck } from "@kuraemon/kcloud-viewutil/dist/src/types"

export type KuratelStartAction = "startScreenShare"

type AccountOnlineStatus =
| {}
| {
  "isOnLine": boolean,
  "isOnTel": boolean
}

type ConstructionStatus =
| {}
| {
  digestJoinAccountIdList: Array<string>,
  joinAccountCount: number,
  patrolCameraCount: number,
  existOnlinePatrolCamera: boolean,
}

export type CallMode = "single" | "multiple"

const DEBOUNCE_MILLIS = 300
const HEARTBEAT_MILLIS = 30 * 1000

export const useKuradenStatusStore = defineStore('kuradenStatus', () => {
  // アカウント オンライン状態
  const __accountOnlineStatusMap = ref<{[accountId: string]: AccountOnlineStatus}>({})

  const addAccountId = (accountId: string) => {
    if (!__accountOnlineStatusMap.value[accountId]) {
      __accountOnlineStatusMap.value[accountId] = {}
    }
  }

  const getAccountStatus = computed(() => {
    return (accountId: string): AccountOnlineStatus => {
      return __accountOnlineStatusMap.value[accountId] || {}
    }
  })

  // 工事の状態
  const __constructionStatusMap = ref<{[constructionId: string]: ConstructionStatus}>({})

  const addConstructionId = (constructionId: string) => {
    if (!__constructionStatusMap.value[constructionId]) {
      __constructionStatusMap.value[constructionId] = {}
    }
  }

  const getConstructionStatus = computed(() => {
    return (constructionId: string): ConstructionStatus => {
      return __constructionStatusMap.value[constructionId] || {}
    }
  })

  const pollingsStore = usePollingsStore()
  const accountProfilesStore = useAccountProfilesStore()
  const __kuraTelJoinRequest = ref<KuraTelJoinRequest | null>(null)
  const isKuradenRinging = computed(() => __kuraTelJoinRequest.value !== null)
  const kuradenJoinRequest = computed(() => {
    if (!__kuraTelJoinRequest.value) return

    const displayName = __kuraTelJoinRequest.value?.startAccount.display?.displayName
    return {
      kuraTelRoomId: __kuraTelJoinRequest.value?.kuraTelRoomId,
      callerAccountId: __kuraTelJoinRequest.value?.startAccount.accountId,
      callerAccountIconUrl: __kuraTelJoinRequest.value?.startAccount.display?.iconUrl,
      message: `${displayName}さんからクラ電です`,
    }   
  })

  const kuradenApiStore = useKuradenApiStore()
  const { liveStatusToken } = storeToRefs(kuradenApiStore)
  const { clearSession, login, statusQuery, heartBeat, makeKuraTelRoom, clientStatusQuerySource } = kuradenApiStore

  // WebSocket 接続管理
  const __webSocket = shallowRef<WebSocket | null>(null)  // 中身検知しないref
  let __reconnectTimer: ReturnType<typeof setTimeout> | null = null

  const disconnectWebSocket = () => {
    if (__reconnectTimer) {
      clearTimeout(__reconnectTimer)
      __reconnectTimer = null
    }
    if (__webSocket.value) {
      __webSocket.value.onclose = null // 再接続ループを防ぐ
      __webSocket.value.close()
      __webSocket.value = null
    }
  }

  const connectWebSocket = (url: string) => {
    disconnectWebSocket()
    const ws = new WebSocket(url)
    __webSocket.value = ws

    ws.onopen = () => {
      console.info('[kuradenStatus] WebSocket connected:', url)
    }

    ws.onmessage = (event) => {
      console.info('[kuradenStatus] WebSocket message received:', event.data)
      // triggerDebounce実行中はスキップ（コールバック末尾でstartKuradenStatusPollingが呼ばれるため）
      if (__triggerInProgress) return
      // トークン取得後、heartBeatポーリングを開始（既存があれば再起動）
      startKuradenStatusPolling()
    }

    ws.onclose = (event) => {
      if (!event.wasClean) {
        // 意図しない切断時は5秒後に再接続
        __reconnectTimer = setTimeout(() => connectWebSocket(url), 5000)
      }
    }

    ws.onerror = (error) => {
      console.error('[kuradenStatus] WebSocket error:', error)
    }
  }

  let __triggerInProgress = false

  const triggerDebounce = () => {
    pollingsStore.startDebounce(
      POLLING_LABELS.DEBOUNCE_SUBSCRIBE_KURADEN_STATUS,
      async () => {
        if (__triggerInProgress) return
        __triggerInProgress = true
        try {
          pollingsStore.stop(POLLING_LABELS.KURADEN_STATUS_POLLING)

          // トークンが未取得の場合は login のみ実行してトークンを取得
          if (!liveStatusToken.value) {
            const loginResponse = await login()
            if (!loginResponse) return

            // WebSocket 接続
            connectWebSocket(loginResponse.webSocketUrl)
          }

          const result = await statusQuery(
            resolveAccountListDigestConstructionIds(),
            callMemberListQueryType.value
          )
          if (!result) return

          // トークン取得後、heartBeatポーリングを開始（既存があれば再起動）
          startKuradenStatusPolling()
        } finally {
          __triggerInProgress = false
        }
      },
      DEBOUNCE_MILLIS
    )
  }

  // 表示順IDリスト
  const __orderedSpecifiedAccountIds = ref<string[]>([])

  const startKuradenStatusPolling = () => {
    pollingsStore.start(
      POLLING_LABELS.KURADEN_STATUS_POLLING,
      async () => {
        if (!liveStatusToken.value) return
        const response = await heartBeat().catch((error) => {
          if (error instanceof TokenError.Expired) {
            // token_expired: トークン期限切れ → 再ログインでトークンを再取得
            clearSession()
            triggerDebounce()
          }
          if (error instanceof TokenError.Invalid) {
            // invalid_token: 旧世代トークン等 → 再ログインでトークンを再取得
            clearSession()
            triggerDebounce()
          }
        })
        if (!response) return

        // オンライン状態マップを更新
        if (response.queryResult) {
          response.queryResult.accountLiveStatuses.forEach(accountStatus => {
            __accountOnlineStatusMap.value[accountStatus.accountId] = {
              isOnLine: accountStatus.isOnLine,
              isOnTel: accountStatus.isOnTel,
            }
          })
          if (response.queryResult.callMemberList) {
            // 配列順を表示順として保存
            if ("constructionIds" in response.queryResult.callMemberList.callMemberListQueryType) {
              __orderedSpecifiedAccountIds.value = response.queryResult.callMemberList.accountIdList
            } else {
              exhaustiveCheck(response.queryResult.callMemberList.callMemberListQueryType)
            }
          }
          // 工事状態を更新
          response.queryResult.accountListDigestConstructions.forEach(constructionStatus => {
            __constructionStatusMap.value[constructionStatus.constructionId] = {
              digestJoinAccountIdList: constructionStatus.digestJoinAccountIdList,
              joinAccountCount: constructionStatus.joinAccountCount,
              patrolCameraCount: constructionStatus.patrolCameraCount,
              existOnlinePatrolCamera: constructionStatus.existOnlinePatrolCamera,
            }
          })
          // 蔵電呼び出し状態を更新
          __kuraTelJoinRequest.value = response.queryResult.kuraTelJoinRequest ?? null
        }
      },
      {
        intervalMillis: HEARTBEAT_MILLIS,
        forceRestart: true,
        continueOnError: true,
      }
    )
  }

  // 工事のアカウント情報をプロフィールキャッシュ・オンライン状態監視に登録する
  const registerConstructionAccounts = (constructionId: string, accountInfoList: { account_id: string, account_name: string, icon_style: IconStyle, icon_url: string }[]) => {
    const accountList = accountInfoList.map(a => ({
      account_id: a.account_id,
      account_name: a.account_name,
      icon_style: a.icon_style,
      icon_url: a.icon_url,
    }))
    accountProfilesStore.updateAccountProfileMap(accountList);

    addConstructionId(constructionId)
    accountList.map(a => a.account_id).forEach(id => {
      addAccountId(id)
    })
  }

  // accountListDigest が必要か（viewMode list表示ではアカウント一覧を表示しないため不要）
  const { viewMode } = storeToRefs(useConstructionListsStore())
  const __accountListDigestEnabled = computed(() => viewMode.value === 'tile')
  watch(__accountListDigestEnabled, () => { triggerDebounce() })

  // accountListDigest取得対象の工事IDリストを算出
  const resolveAccountListDigestConstructionIds = (): string[] => {
    if (!__accountListDigestEnabled.value) return []
    const allConstructionIds = Object.keys(__constructionStatusMap.value)
    if (visibleConstructionIds.value === null) return allConstructionIds
    return allConstructionIds.filter(id => id in visibleConstructionIds.value!)
  }

  const route = useRoute()
  const isConstructionListRoute = computed(() => (route.name === 'ConstructionList'))
  const isConstructionListChildRoute = computed(() =>
    route.matched.some(r => r.name === 'ConstructionList') && route.matched.length > 1
  )
  // 工事一覧でフォーカスされている工事
  const __focusedConstructionId = ref<string>()
  const setFocusedConstructionId = (constructionId?: string) => {
    __focusedConstructionId.value = constructionId
  }
  const denwaConstructionId = computed(() => {
    if (isConstructionListRoute.value) {
      if (viewMode.value === 'list') return undefined
      if (viewMode.value === "tile") return __focusedConstructionId.value
      exhaustiveCheck(viewMode.value)
    }
    if (isConstructionListChildRoute.value) {
      return route.params.cloudConstructionId as string | undefined
    }
    return undefined
  })
  const callMemberListQueryType = computed(() => {
    if (denwaConstructionId.value) {
      return { constructionIds: [ denwaConstructionId.value ] }
    }
    return null
  })
  watch(
    callMemberListQueryType,
    () => { triggerDebounce() },
    { deep: true }
  )

  const { cloudConstructionList, cloudConstructionListRevision, constructionMemberMap, visibleConstructionIds } = storeToRefs(useConstructionListsStore())

  // 工事一覧の自動同期（工事IDの登録のみ。アカウント情報はconstructionMemberMapから取得）
  watch(cloudConstructionListRevision, () => {
    cloudConstructionList.value?.forEach(c => {
      addConstructionId(c.cloud_construction_id)
    })

    // フォーカス中の工事が一覧から消えていたらクリア
    if (__focusedConstructionId.value) {
      const exists = cloudConstructionList.value?.some(
        c => c.cloud_construction_id === __focusedConstructionId.value
      )
      if (!exists) {
        __focusedConstructionId.value = undefined
      }
    }
  }, { immediate: true })

  // 工事メンバー情報の自動同期（constructionMemberMapが更新されたらアカウント情報を登録）
  watch(constructionMemberMap, (newMap) => {
    Object.entries(newMap).forEach(([constructionId, memberInfo]) => {
      const allAccounts = [
        ...memberInfo.manager_account_info_list,
        ...memberInfo.normal_account_info_list,
      ]
      registerConstructionAccounts(constructionId, allAccounts)
    })
  }, { deep: true })

  // 工事単体の自動同期
  const { cloudConstructionInfo } = storeToRefs(useConstructionStore())
  watch(cloudConstructionInfo, (c) => {
    if (!c) return
    registerConstructionAccounts(c.cloud_construction_id, c.account_info_list)
  }, { immediate: true })

  // 画面に表示中の工事IDフィルタ（constructionListsStoreから読み取り）
  watch(visibleConstructionIds, () => { triggerDebounce() })

  // ソートされたプロフィールを返す
  const toSortedProfiles = (accountIds: string[]) => {
    const orderedAccountIds = __orderedSpecifiedAccountIds.value
    if (orderedAccountIds.length === 0) return []

    const profiles = accountProfilesStore.getAccountProfileList(accountIds)
    const orderMap = new Map(orderedAccountIds.map((id, i) => [id, i]))

    return [...profiles]
      .filter(p => orderMap.has(p.account_id))
      .sort((a, b) => {
        const ia = orderMap.get(a.account_id) ?? Infinity
        const ib = orderMap.get(b.account_id) ?? Infinity
        return ia - ib
      })
  }

  // アカウントプロフィールリストを返す
  const getAccountProfiles = (constructionId: string) => {
    // 工事メンバーマップから取得（lazy ロード済みの場合）
    const memberInfo = constructionMemberMap.value[constructionId]
    if (memberInfo) {
      const accountIds = [
        ...memberInfo.manager_account_info_list,
        ...memberInfo.normal_account_info_list,
      ].map(a => a.account_id)
      return toSortedProfiles(accountIds)
    }

    // フォールバック: 単体工事APIからロードされた情報
    if (cloudConstructionInfo.value && cloudConstructionInfo.value.cloud_construction_id === constructionId) {
      const accountIds = cloudConstructionInfo.value.account_info_list.map(c => c.account_id)
      return toSortedProfiles(accountIds)
    }

    return []
  }


  const phoneCallMode = ref<CallMode>("single")
  const setPhoneCallMode = (mode: CallMode) => {
    phoneCallMode.value = mode;
  }

  // 蔵電ダイアログの呼び出し元
  type KuradenCalledFrom = 'photoDetail' | 'zumenPageDetail' | 'kokubanDetail'
  const phoneCallDialog = ref(false)
  const phoneCallTargetAccountIds = ref<string[] | null>(null)
  const phoneCallCalledFrom = ref<KuradenCalledFrom | null>(null)
  const showPhoneCallDialog = (calledFrom: KuradenCalledFrom, targetAccountIds?: string[]) => {
    if (!denwaConstructionId.value) return
    setPhoneCallMode("single")
    phoneCallTargetAccountIds.value = targetAccountIds ?? null
    phoneCallCalledFrom.value = calledFrom
    phoneCallDialog.value = true
  }
  const showMembersPhoneCall = (sharedAccountIds: string[], calledFrom: KuradenCalledFrom) => {
    const accountInfoList = cloudConstructionInfo.value?.account_info_list ?? []
    const targetAccountIds = accountInfoList
      .filter(a =>
        a.role_type === 'manager' ||
        sharedAccountIds.includes(a.account_id)
      )
      .map(a => a.account_id)
    showPhoneCallDialog(calledFrom, targetAccountIds)
  }
  const hidePhoneCallDialog = () => {
    phoneCallDialog.value = false
    phoneCallTargetAccountIds.value = null
  }

  const accountStatusDialog = ref<{ account: ConstructionAccountInfo } | undefined>(undefined)
  const showAccountStatusDialog = (account: ConstructionAccountInfo) => {
    accountStatusDialog.value = { account }
  }
  const hideAccountStatusDialog = () => {
    accountStatusDialog.value = undefined
  }

  const { lock: operationLock, unlock: operationUnlock } = useOperationLockStore()
  /**
   * 蔵衛門電話 発信シーケンス
   * 1. makeKuraTelRoom でルーム作成 → kuraTelRoomId 取得
   * 2. 別タブで蔵電通話ページを起動
   * 3. clientStatusQuerySource で openKuraTelViewPage を通知
   */
  const startKuraTelCall = async (callAccountIds: Array<string>, newWindow: Window | null) => {
    // トークンがなければ取得完了を待つ
    if (!liveStatusToken.value) {
      await loadedValuePromise(() => liveStatusToken.value).catch(() => {})
    }
    if (!liveStatusToken.value) { newWindow?.close(); return }

    // 1. ルーム作成
    const result = await makeKuraTelRoom(callAccountIds)
    if (!result) { newWindow?.close(); return }

    const { kuraTelRoomId } = result

    const action: KuratelStartAction = "startScreenShare"

    // 2. 別タブで蔵電通話ページを起動（newWindow は呼び出し元でユーザー操作中に開いたもの）
    const kuraTelPageUrl = `${import.meta.env.VITE_APP_KURADEN_FRONTEND_URL}?token=${encodeURIComponent(liveStatusToken.value)}&roomId=${encodeURIComponent(kuraTelRoomId)}&action=${action}`
    if (newWindow) newWindow.location.href = kuraTelPageUrl

    // 3. openKuraTelViewPage を通知
    await clientStatusQuerySource({
      kuraTelRoomId,
      status: { openKuraTelViewPage: {} },
    })

    // 画面共有で発信した場合は操作禁止モードを有効化
    if (action === "startScreenShare") {
      operationLock()
      // 蔵電ウィンドウが閉じられたら操作禁止を解除
      if (newWindow) {
        pollingsStore.start(
          POLLING_LABELS.KURATEL_WINDOW_CLOSED,
          async () => newWindow.closed,
          {
            intervalMillis: 2000,
            forceRestart: true,
            terminateCondition: (closed) => {
              if (closed) operationUnlock()
              return !!closed
            },
          }
        )
      }
    }
  }

  // 蔵衛門電話 受信ダイアログ
  const receiveCallDialog = ref<boolean>(false)

  const showReceiveCallDialog = () => {
    receiveCallDialog.value = true
  }
  const hideReceiveCallDialog = () => {
    receiveCallDialog.value = false
  }

  // 自分への電話エラーダイアログ
  const selfCallErrorDialog = ref<boolean>(false)
  const showSelfCallErrorDialog = () => {
    selfCallErrorDialog.value = true
  }
  const hideSelfCallErrorDialog = () => {
    selfCallErrorDialog.value = false
  }

  /** 蔵衛門電話 拒否 */
  const rejectKuraTelCall = async () => {
    if (!liveStatusToken.value) return
    if (!__kuraTelJoinRequest.value) return

    const { kuraTelRoomId } = __kuraTelJoinRequest.value

    await clientStatusQuerySource({
      kuraTelRoomId,
      status: { reject: {} },
    })

    hideReceiveCallDialog()
  }

  /**
   * 蔵衛門電話 受信時の通話ページURL取得（同期的に呼び出すこと）
   */
  const getKuraTelJoinUrl = () => {
    if (!liveStatusToken.value) return undefined
    if (!__kuraTelJoinRequest.value) return undefined
    const { kuraTelRoomId } = __kuraTelJoinRequest.value
    return `${import.meta.env.VITE_APP_KURADEN_FRONTEND_URL}?token=${encodeURIComponent(liveStatusToken.value)}&roomId=${encodeURIComponent(kuraTelRoomId)}&action=join`
  }

  /**
   * 蔵衛門電話 受信シーケンス
   * 1. 呼び出し元でユーザー操作中に window.open すること（ポップアップブロック回避）
   * 2. clientStatusQuerySource で openKuraTelViewPage を通知
   */
  const acceptKuraTelCall = async () => {
    if (!liveStatusToken.value) return
    if (!__kuraTelJoinRequest.value) return

    const { kuraTelRoomId } = __kuraTelJoinRequest.value

    // openKuraTelViewPage を通知
    await clientStatusQuerySource({
      kuraTelRoomId,
      status: { openKuraTelViewPage: {} },
    })

    // ダイアログを閉じる
    hideReceiveCallDialog()
  }

  // クリーンアップ
  const cleanupKuradenStatus = () => {
    disconnectWebSocket()
    pollingsStore.stop(POLLING_LABELS.DEBOUNCE_SUBSCRIBE_KURADEN_STATUS)
    pollingsStore.stop(POLLING_LABELS.KURADEN_STATUS_POLLING)
    pollingsStore.stop(POLLING_LABELS.KURATEL_WINDOW_CLOSED)
    operationUnlock()
    clearSession()
    __accountOnlineStatusMap.value = {}
    __constructionStatusMap.value = {}
    useConstructionListsStore().clearVisibleConstructionIds()
    __orderedSpecifiedAccountIds.value = []
    console.info("kuradenStatus cleaned up.")
  }

  // ログイン状態の監視（ログイン・ログアウト・ページリフレッシュ を一元管理）
  const { addLoginCompletedListener } = useLoginStore()
  const { accountInfo } = storeToRefs(useLoginStore())

  // 再ログイン時（accountInfoがnullにならずに切り替わるケース）のクリーンアップ
  addLoginCompletedListener(cleanupKuradenStatus)

  watch(accountInfo, (newValue) => {
    if (!newValue) {
      cleanupKuradenStatus()
    } else {
      triggerDebounce()
    }
  }, { immediate: true })

  // 受電ダイアログの表示（ログイン済み・セッション不要ページ以外で表示）
  watch(
    () => {
      if (!kuradenJoinRequest.value) return
      const { callerAccountId, kuraTelRoomId } = kuradenJoinRequest.value
      return { callerAccountId, kuraTelRoomId }
    },
    (newVal) => {
      if (!accountInfo.value) return
      if (route.meta.isPublic) return
      if (newVal) {
        showReceiveCallDialog()
      } else {
        hideReceiveCallDialog()
      }
    },
    { deep: true }
  )

  // 自アカウントの通話状態
  const currentAccountIsOnTel = computed(() => {
    const currentAccountId = accountInfo.value?.account_id
    if (!currentAccountId) return undefined
    const status = __accountOnlineStatusMap.value[currentAccountId]
    if (!status || !('isOnTel' in status)) return undefined
    return status.isOnTel
  })
  // 自アカウントの通話終了を検知して操作禁止を解除
  watch(currentAccountIsOnTel, (isOnTel, prevIsOnTel) => {
    if (prevIsOnTel === true && isOnTel === false) {
      operationUnlock()
      pollingsStore.stop(POLLING_LABELS.KURATEL_WINDOW_CLOSED)
    }
  })

  return {
    __accountOnlineStatusMap,
    addAccountId,
    getAccountStatus,
    getAccountProfiles,
    __orderedSpecifiedAccountIds,

    getConstructionStatus,

    __focusedConstructionId,
    setFocusedConstructionId,
    denwaConstructionId,

    isConstructionListRoute,
    isConstructionListChildRoute,

    phoneCallMode,
    setPhoneCallMode,

    phoneCallDialog,
    phoneCallTargetAccountIds,
    phoneCallCalledFrom,
    showMembersPhoneCall,
    hidePhoneCallDialog,

    accountStatusDialog,
    showAccountStatusDialog,
    hideAccountStatusDialog,

    __kuraTelJoinRequest,
    isKuradenRinging,
    kuradenJoinRequest,

    startKuraTelCall,

    receiveCallDialog,
    showReceiveCallDialog,
    hideReceiveCallDialog,

    selfCallErrorDialog,
    showSelfCallErrorDialog,
    hideSelfCallErrorDialog,

    getKuraTelJoinUrl,
    acceptKuraTelCall,
    rejectKuraTelCall,
  }
})
